<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.report_products") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card class="no-border-card" body-classes="px-0">
				<div class="px-4">
					<pos-report-filter v-model="filter" @search="load"></pos-report-filter>
					<div class="mb-3 d-flex flex-wrap align-items-center">
						<!-- phase 3: POS product level | WMS component level -->
						<el-radio-group v-model="level" size="small" class="mr-4 mb-2" @change="load">
							<el-radio-button label="pos">{{ $t("pos.report.level_pos") }}</el-radio-button>
							<el-radio-button label="wms">{{ $t("pos.report.level_wms") }}</el-radio-button>
						</el-radio-group>
						<template v-if="level === 'pos'">
							<label class="form-control-label mr-3 mb-2">{{ $t("pos.report.sort_by") }}</label>
							<el-radio-group v-model="sort" size="small" class="mb-2" @change="load">
								<el-radio-button label="qty">{{ $t("pos.report.sort_qty") }}</el-radio-button>
								<el-radio-button label="amount">{{ $t("pos.report.sort_amount") }}</el-radio-button>
							</el-radio-group>
						</template>
					</div>

					<pos-bar-chart
						:rows="chartRows"
						:series="chartSeries"
						:money="level === 'pos' && sort === 'amount'"
						:empty-text="$t('pos.report.no_data')"
						class="mb-4"
					></pos-bar-chart>
				</div>

				<el-table v-if="level === 'pos'" :data="rows" v-loading="loading" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.report.product')" prop="name" min-width="220"></el-table-column>
					<el-table-column :label="$t('pos.report.code')" prop="code" width="120"></el-table-column>
					<el-table-column :label="$t('pos.report.qty_pieces')" min-width="110" align="right">
						<template slot-scope="{ row }"><strong>{{ int(row.qty) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.amount')" min-width="130" align="right">
						<template slot-scope="{ row }">{{ money(row.amount) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.qty_special')" min-width="140" align="right">
						<template slot-scope="{ row }">{{ int(row.qty_special_price) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.bills')" min-width="90" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
				</el-table>
				<el-table v-else :data="rows" v-loading="loading" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.report.wms_product')" prop="name" min-width="240"></el-table-column>
					<el-table-column :label="$t('pos.report.wms_code')" prop="man_pn" width="140"></el-table-column>
					<el-table-column :label="$t('pos.report.qty_pieces')" min-width="110" align="right">
						<template slot-scope="{ row }"><strong>{{ int(row.qty) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.bills')" min-width="90" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
				</el-table>
				<p class="text-muted small mt-3 mb-0">{{ $t("pos.report.products_note") }}</p>
			</card>
		</div>
	</div>
</template>

<script>
import { Table, TableColumn, RadioGroup, RadioButton } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt } from "@/utils/pos";
import posPageMixin, { posDefaultFilter } from "./posPageMixin";
import PosReportFilter from "./PosReportFilter.vue";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosBarChart from "./PosBarChart.vue";

export default {
	name: "PosReportProducts",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosReportFilter,
		PosInstallAlert,
		PosBarChart,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[RadioGroup.name]: RadioGroup,
		[RadioButton.name]: RadioButton
	},
	data() {
		return { filter: posDefaultFilter(), level: "pos", sort: "qty", rows: [] };
	},
	computed: {
		chartSeries() {
			if (this.level === "pos" && this.sort === "amount") return [{ key: "v", label: this.$t("pos.report.amount"), color: "#11cdef" }];
			return [{ key: "v", label: this.$t("pos.report.qty_pieces"), color: "#11cdef" }];
		},
		chartRows() {
			const amt = this.level === "pos" && this.sort === "amount";
			return this.rows.slice(0, 10).map((r) => ({ label: r.name, values: { v: amt ? Number(r.amount) || 0 : Number(r.qty) || 0 } }));
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		async load(extra) {
			const p = this.posParams(this.filter);
			if (!p) return;
			p.level = this.level;
			if (this.level === "pos") p.sort = this.sort;
			this.loading = true;
			try {
				const res = await PosService.PosReportProducts({ ...p, ...(extra || {}) });
				this.rows = (res.data && res.data.Data) || [];
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		async exportXlsx() {
			const p = this.posParams(this.filter);
			if (!p) return;
			p.level = this.level;
			if (this.level === "pos") p.sort = this.sort;
			this.loading = true;
			try {
				const res = await PosService.PosReportProducts({ ...p, export: 1 });
				const t = (k) => this.$t(k);
				const columns =
					this.level === "pos"
						? [
							{ label: t("pos.report.product"), value: "name" },
							{ label: t("pos.report.code"), value: "code" },
							{ label: t("pos.report.qty_pieces"), value: "qty" },
							{ label: t("pos.report.amount"), value: (r) => Number(r.amount) },
							{ label: t("pos.report.qty_special"), value: "qty_special_price" },
							{ label: t("pos.report.bills"), value: "bill_count" }
						]
						: [
							{ label: t("pos.report.wms_product"), value: "name" },
							{ label: t("pos.report.wms_code"), value: "man_pn" },
							{ label: t("pos.report.qty_pieces"), value: "qty" },
							{ label: t("pos.report.bills"), value: "bill_count" }
						];
				this.posExport(this.level === "pos" ? "products" : "products-wms", p, [{
					name: this.level === "pos" ? t("pos.report.level_pos") : t("pos.report.level_wms"),
					rows: (res.data && res.data.Data) || [],
					columns
				}]);
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>
